<template>
  <div class="timeframe-dropdown">
    <select 
      v-model="selectedValue"
      @change="handleChange"
      class="timeframe-select"
    >
      <option 
        v-for="option in timeframeOptions" 
        :key="option.label"
        :value="JSON.stringify(option.value)"
      >
        {{ option.label }}
      </option>
    </select>
    <icon name="chevron-down" class="timeframe-dropdown__icon" />
  </div>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'TimeframeDropdown',
  props: {
    modelValue: {
      type: Object,
      required: true
    }
  },
  emits: ['update:model-value'],
  data() {
    return {
      selectedValue: JSON.stringify(this.modelValue.value)
    }
  },
  watch: {
    modelValue: {
      handler(newValue) {
        this.selectedValue = JSON.stringify(newValue.value)
      },
      immediate: true
    }
  },
  computed: {
    timeframeOptions() {
      const currentYear = new Date().getFullYear()
      const options = []

      // Add years and quarters from 2020 to current year
      for (let year = 2020; year <= currentYear; year++) {
        // Add full year option
        options.push({
          label: year.toString(),
          value: { year, quarter: null }
        })

        // Add quarters for each year
        for (let quarter = 1; quarter <= 4; quarter++) {
          // Don't add future quarters
          if (year === currentYear && quarter > Math.ceil((new Date().getMonth() + 1) / 3)) {
            continue
          }

          options.push({
            label: `Q${quarter} ${year}`,
            value: { year, quarter }
          })
        }
      }

      // Sort options in descending order (most recent first)
      return options.sort((a, b) => {
        const yearA = a.value.year
        const yearB = b.value.year
        const quarterA = a.value.quarter ?? 5 // Use 5 for full year to sort it after quarters
        const quarterB = b.value.quarter ?? 5

        if (yearA !== yearB) {
          return yearB - yearA // Sort years descending
        }
        return quarterB - quarterA // Sort quarters descending within same year
      })
    }
  },
  methods: {
    handleChange() {
      const selectedValue = JSON.parse(this.selectedValue)
      const selectedOption = this.timeframeOptions.find(option => 
        option.value.year === selectedValue.year && 
        option.value.quarter === selectedValue.quarter
      )
      this.$emit('update:model-value', selectedOption)
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../../scss/_variables";

.timeframe-dropdown {
  display: inline-block;
  vertical-align: top;
  margin-right: 5px;
  position: relative;
  min-width: 150px;

  .timeframe-select {
    appearance: none;
    background: white;
    border: 1px solid $color-borders;
    border-radius: 0;
    padding: 4px 32px 4px 4px;
    width: 100%;
    font-size: 14px;
    color: #444;
    min-height: 36px;
    line-height: 18px;
    cursor: pointer;
    transition: none;

    &:hover {
      border-color: #aaa;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-community);
      box-shadow: 0 0 0 2px var(--primary-community);
    }
  }

  .timeframe-dropdown__icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    opacity: 0.5;
    width: 14px;
    height: 14px;

    path {
      fill: #444;
    }
  }

  &:hover .timeframe-dropdown__icon {
    opacity: 0.8;
  }
}
</style>
